import React, { useEffect, useMemo, useState } from "react";
import { Alert, Button, Spin } from "antd";
import CardsBorder from "../../../components/Cards/CardsBorder";
import TextStyles from "../../../components/Elements/TextStyles/TextStyles";
import CustomSwitch from "../../../components/Elements/CustomSwitch/CustomSwitch";
import { patchApi } from "../../../api/api_instance";
import { API_URLS } from "../../../api/apiUrls";
import PopupCard from "../../../components/Cards/PopupCard/PopupCard";
import { useLocation } from "react-router-dom";

const CustomerLevel = ({
  customerId,
  customerDetail,
  onSuccess
}) => {
  const location = useLocation();
  const isCustomerPage = location.pathname.includes("/customer/customer-page/");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [popup, setPopup] = useState({
    visible: false,
    type: "success",
    title: "",
    message: "",
  });
  const [newAmount, setNewAmount] = useState(0);
  const [newMvAmount, setNewMvAmount] = useState(0);
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [mvDate, setMvDate] = useState(new Date().toISOString().slice(0, 10));

  const showPopup = (type, title, message) => {
    setPopup({ visible: true, type, title, message });
    setTimeout(() => setPopup((prev) => ({ ...prev, visible: false })), 3000);
  };

  const total = Number(customerDetail?.license?.sub_admin?.total ?? 0);
  const used = Number(customerDetail?.license?.sub_admin?.used ?? 0);
  const available = Number(customerDetail?.license?.sub_admin?.unused ?? total);
  const usage = total > 0 ? `${Math.round((used / total) * 100)}%` : "0%";

  const mvTotal = Number(customerDetail?.license?.management_view?.total ?? 0);
  const mvUsed = Number(customerDetail?.license?.management_view?.used ?? 0);
  const mvAvailable = Number(customerDetail?.license?.management_view?.unused ?? mvTotal);
  const mvUsage = mvTotal > 0 ? `${Math.round((mvUsed / mvTotal) * 100)}%` : "0%";

  useEffect(() => {
    setNewAmount(0);
    setNewMvAmount(0);
    setDate(new Date().toISOString().slice(0, 10));
    setMvDate(new Date().toISOString().slice(0, 10));
  }, [customerId]);

  const featureSettings = useMemo(
    () => [
      {
        key: "offsetGo_question_bank_access",
        title: "Access To OffsetGo Question Bank",
        description:
          "When Enabled, Customer Can Use Any Form From The OffsetGo Library.",
        enabled: false,
        date: "",
      },
      {
        key: "dynamic_form_creation_access",
        title: "Creation Of A Dynamic Form",
        description:
          "When Enabled, Customer Can Create Custom Forms Using Pre-Approved Questions.",
        enabled: false,
        date: "",
      },
    ],
    [],
  );

  const handleSave = async () => {
    if (!customerId) return;

    const subAdminCount = Number(newAmount) || 0;
    const mvCount = Number(newMvAmount) || 0;

    if (subAdminCount === 0 && mvCount === 0) {
      setError("Please enter a new value for at least one license type.");
      showPopup("error", "Error", "Please enter a new value for at least one license type.");
      return;
    }

    try {
      setSaving(true);
      setError("");

      const promises = [];

      if (subAdminCount > 0) {
        promises.push(
          patchApi({
            apiUrl: API_URLS.ADD_CUSTOMER_SUB_ADMIN_LICENSE(customerId),
            data: {
              action: "add",
              count: subAdminCount,
              date: date,
            },
          })
        );
      }

      if (mvCount > 0) {
        promises.push(
          patchApi({
            apiUrl: API_URLS.ADD_CUSTOMER_MANAGEMENT_VIEW_LICENSE(customerId),
            data: {
              action: "add",
              count: mvCount,
              date: mvDate,
            },
          })
        );
      }

      await Promise.all(promises);

      showPopup("success", "Success", "Customer license updated successfully");
      setNewAmount(0);
      setNewMvAmount(0);
      setDate(new Date().toISOString().slice(0, 10));
      setMvDate(new Date().toISOString().slice(0, 10));
      if (onSuccess) {
        onSuccess();
      }
    } catch (err) {
      setError(err?.message || "Failed to update customer license");
      showPopup(
        "error",
        "Error",
        err?.message || "Failed to update customer license",
      );
    } finally {
      setSaving(false);
    }
  };

  if (!customerDetail) {
    return (
      <div className="mx-4">
        <CardsBorder>
          <div className="py-8 flex justify-center">
            <Spin />
          </div>
        </CardsBorder>
      </div>
    );
  }

  return (
    <>
      <div className="mx-4">
        <CardsBorder>
          <div className="mx-4">
            <TextStyles
              color="gradient"
              style={{ fontSize: "20px", fontWeight: 500 }}
            >
              CUSTOMER-LEVEL LICENSES
            </TextStyles>
            <p className="font-light mt-[-10px]">
              These licenses apply globally across all projects for this
              customer. Enter the number of licenses below.
            </p>
          </div>

          {error && (
            <div className="px-4 pt-4">
              <Alert type="error" message={error} showIcon />
            </div>
          )}

          <div className="py-2 mt-2 rounded-md">
            <table className="w-full text-left">
              <thead className="bg-[#E6EEEC] text-sm font-semibold text-[#1F2A37]">
                <tr>
                  <th className="px-2 py-2 text-center">LICENSE TYPE</th>
                  <th className="px-2 py-2 text-center">ISSUED</th>
                  <th className="px-2 py-2 text-center">USED</th>
                  <th className="px-2 py-2 text-center">AVAILABLE</th>
                  <th className="px-2 py-2 text-center">USAGE</th>
                  <th className="px-2 py-2 text-center">DATE</th>
                  <th className="px-2 py-2 text-center">NEW VALUE</th>
                </tr>
              </thead>
              <tbody className="text-sm text-black px-4 text-center">
                <tr className="border-b border-gray-300">
                  <td className="px-2 py-3">Sub-Admin</td>
                  <td className="px-2">{total}</td>
                  <td className="px-2">{used}</td>
                  <td className="px-2">{available}</td>
                  <td className="px-2">
                    <div className="w-full bg-gray-200 rounded h-2">
                      <div
                        className="bg-[#06513D] h-2 rounded"
                        style={{ width: usage }}
                      />
                    </div>
                    <span className="text-xs">{usage}</span>
                  </td>
                  <td className="px-2">
                    <input
                      type="date"
                      value={date}
                      onChange={(e) => setDate(e.target.value)}
                      className="border border-black rounded px-2 py-1 w-[130px]"
                    />
                  </td>
                  <td className="px-2">
                    <input
                      type="number"
                      min="1"
                      placeholder="0"
                      value={newAmount === 0 ? "" : newAmount}
                      onChange={(e) =>
                        setNewAmount(Number(e.target.value) || 0)
                      }
                      className="border border-black rounded px-2 py-1 w-[60px]"
                    />
                  </td>
                </tr>
                <tr className="border-b border-gray-300">
                  <td className="px-2 py-3">Management view</td>
                  <td className="px-2">{mvTotal}</td>
                  <td className="px-2">{mvUsed}</td>
                  <td className="px-2">{mvAvailable}</td>
                  <td className="px-2">
                    <div className="w-full bg-gray-200 rounded h-2">
                      <div
                        className="bg-[#06513D] h-2 rounded"
                        style={{ width: mvUsage }}
                      />
                    </div>
                    <span className="text-xs">{mvUsage}</span>
                  </td>
                  <td className="px-2">
                    <input
                      type="date"
                      value={mvDate}
                      onChange={(e) => setMvDate(e.target.value)}
                      className="border border-black rounded px-2 py-1 w-[130px]"
                    />
                  </td>
                  <td className="px-2">
                    <input
                      type="number"
                      min="1"
                      placeholder="0"
                      value={newMvAmount === 0 ? "" : newMvAmount}
                      onChange={(e) =>
                        setNewMvAmount(Number(e.target.value) || 0)
                      }
                      className="border border-black rounded px-2 py-1 w-[60px]"
                    />
                  </td>
                </tr>
              </tbody>
            </table>

            {!isCustomerPage && (
              <div className="text-center py-5">
                <Button
                  type="primary"
                  style={{ background: "#06513D" }}
                  className="text-sm font-bold px-6 py-5"
                  loading={saving}
                  onClick={handleSave}
                >
                  Save Change
                </Button>
              </div>
            )}
          </div>
        </CardsBorder>

        {/* <TextStyles
          color="gradient"
          style={{ fontSize: "20px", fontWeight: 500 }}
        >
          <p className="mx-4 mt-10"> LICENSES & FEATURES MANAGEMENT</p>
        </TextStyles>

        <CardsBorder>
          {featureSettings.map((setting, index) => (
            <div key={setting.key}>
              <div className="flex justify-between mx-10">
                <div>
                  <p className="text-lg font-[300]">{setting.title}</p>
                  <p className="mt-[-10px] text-gray-500">
                    {setting.description}
                  </p>
                </div>

                <div className="flex gap-20 items-center">
                  <input
                    type="date"
                    value={setting.date}
                    readOnly
                    className="border border-black rounded px-2 py-1 w-[120px]"
                  />

                  <CustomSwitch checked={setting.enabled} onChange={() => {}} />
                </div>
              </div>

              <div className="pb-3 px-4">
                <hr />
              </div>
            </div>
          ))}

          {!isCustomerPage && (
            <div className="text-center py-5">
              <Button
                type="primary"
                style={{ background: "#06513D" }}
                className="text-sm font-bold px-6 py-5"
                onClick={() => {}}
              >
                Save Change
              </Button>
            </div>
          )}
        </CardsBorder> */}
      </div>

      {/* Popup Notification */}
      {popup.visible && (
        <div className="fixed inset-0 bg-black/30 z-[9999] flex items-center justify-center">
          <PopupCard
            type={popup.type}
            title={popup.title}
            message={popup.message}
            onClose={() => setPopup((prev) => ({ ...prev, visible: false }))}
          />
        </div>
      )}
    </>
  );
};

export default CustomerLevel;
